import {
    syncCampaignHostFlutterwavePlansUrl,
    syncCreatorFlutterwavePlansUrl,
    syncEventHostFlutterwavePlansUrl,
} from '../configurations/api/url';

export type FlutterwaveSyncKind = 'campaign_host' | 'event_host' | 'creator';

export const FLUTTERWAVE_SYNC_KIND_OPTIONS: Array<{
    id: FlutterwaveSyncKind;
    label: string;
    description: string;
}> = [
    {
        id: 'campaign_host',
        label: 'Campaign host',
        description: 'Brand / campaign host packages (monthly recurring)',
    },
    {
        id: 'event_host',
        label: 'Event host',
        description: 'Meeteor event host marketplace packages',
    },
    {
        id: 'creator',
        label: 'Plans',
        description: 'Creator subscription plans (Starter / Pro / …)',
    },
];

/** Flutterwave payment-plan intervals (dashboard / API). */
export const FLUTTERWAVE_SYNC_INTERVAL_OPTIONS: Array<{
    id: string;
    label: string;
}> = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'quarterly', label: 'Quarterly' },
    { id: 'bi-annually', label: 'Bi-annually' },
    { id: 'yearly', label: 'Yearly' },
];

/** Normalize catalog / UI interval aliases → Flutterwave API interval. */
export function normalizeFlutterwaveInterval(raw?: string | null): string {
    const v = String(raw || '')
        .trim()
        .toLowerCase();
    if (!v) return 'monthly';
    if (v === 'day' || v === 'daily') return 'daily';
    if (v === 'week' || v === 'weekly') return 'weekly';
    if (v === 'month' || v === 'monthly') return 'monthly';
    if (v === 'quarter' || v === 'quarterly') return 'quarterly';
    if (
        v === 'bi-annually' ||
        v === 'biannually' ||
        v === 'bi_annually' ||
        v === 'semi-annually' ||
        v === 'semiannually'
    ) {
        return 'bi-annually';
    }
    if (v === 'year' || v === 'yearly' || v === 'annually' || v === 'annual') {
        return 'yearly';
    }
    return v;
}

export type FlutterwaveCatalogItem = {
    id: string;
    name: string;
    slug?: string;
    billing_type?: string;
    list_price_cents: number;
    interval?: string | null;
    flutterwave_plan_code?: string | null;
    flutterwave_plan_codes?: Record<string, string> | null;
    source?: string | null;
    currency?: string | null;
};

export type FlutterwaveSyncPlanRow = {
    currency: string;
    plan_code?: string;
    skipped?: boolean;
    dry_run?: boolean;
    error?: string;
    amount_major?: number;
    amount_minor?: number;
    currency_mismatch?: boolean;
    reason?: string;
    name?: string;
};

export type FlutterwaveSyncResult = {
    package_id?: string | null;
    package_kind: FlutterwaveSyncKind;
    name?: string;
    interval?: string;
    usd_amount_cents?: number;
    dry_run?: boolean;
    flutterwave_plan_code?: string | null;
    flutterwave_plan_codes?: Record<string, string>;
    plans?: FlutterwaveSyncPlanRow[];
};

export function unwrapFlutterwaveList(response: unknown): unknown[] {
    if (response == null) return [];
    if (Array.isArray(response)) return response;
    const root = response as Record<string, unknown>;
    const data = root.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
        const inner = data as Record<string, unknown>;
        if (Array.isArray(inner.data)) return inner.data;
        if (Array.isArray(inner.result)) return inner.result;
        if (Array.isArray(inner.docs)) return inner.docs;
    }
    if (Array.isArray(root.result)) return root.result;
    return [];
}

function unwrapFlutterwaveData<T>(response: unknown): T {
    if (response == null) return {} as T;
    const root = response as Record<string, unknown>;
    if (root.data && typeof root.data === 'object') {
        const inner = root.data as Record<string, unknown>;
        if (inner.data && typeof inner.data === 'object' && !Array.isArray(inner.data)) {
            return inner.data as T;
        }
        return root.data as T;
    }
    return root as T;
}

export function normalizeFlutterwavePlanCodes(raw: unknown): Record<string, string> | null {
    if (!raw || typeof raw !== 'object') return null;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (k && v != null && String(v).trim()) out[String(k).toLowerCase()] = String(v).trim();
    }
    return Object.keys(out).length ? out : null;
}

export function normalizeFlutterwaveCatalogItem(raw: Record<string, unknown>): FlutterwaveCatalogItem | null {
    const id = String(raw._id ?? raw.id ?? '').trim();
    if (!id) return null;
    const list = Number(raw.list_price_cents ?? raw.listPriceCents ?? raw.amount ?? 0) || 0;
    return {
        id,
        name: String(raw.name || 'Untitled'),
        slug: raw.slug != null ? String(raw.slug) : undefined,
        billing_type: raw.billing_type != null ? String(raw.billing_type) : undefined,
        list_price_cents: list,
        interval: raw.interval != null ? String(raw.interval) : null,
        flutterwave_plan_code:
            raw.flutterwave_plan_code != null
                ? String(raw.flutterwave_plan_code)
                : raw.plan_code != null && raw.source === 'flutterwave'
                ? String(raw.plan_code)
                : null,
        flutterwave_plan_codes: normalizeFlutterwavePlanCodes(raw.flutterwave_plan_codes),
        source: raw.source != null ? String(raw.source) : null,
        currency: raw.currency != null ? String(raw.currency) : null,
    };
}

export type SyncFlutterwavePlansInput = {
    kind: FlutterwaveSyncKind;
    packageId?: string | null;
    usd_amount_cents: number;
    name?: string;
    interval?: string;
    currencies?: string[];
    dry_run?: boolean;
    /** false = recreate / overwrite for price updates */
    skip_existing?: boolean;
};

export function syncFlutterwavePlansUrl(kind: FlutterwaveSyncKind, packageId?: string | null): string {
    if (kind === 'campaign_host') {
        if (!packageId) throw new Error('Campaign host package id is required');
        return syncCampaignHostFlutterwavePlansUrl(packageId);
    }
    if (kind === 'event_host') {
        if (!packageId) throw new Error('Event host package id is required');
        return syncEventHostFlutterwavePlansUrl(packageId);
    }
    return syncCreatorFlutterwavePlansUrl(packageId || undefined);
}

export function normalizeFlutterwaveSyncResult(response: unknown, kind: FlutterwaveSyncKind): FlutterwaveSyncResult {
    const data = unwrapFlutterwaveData<FlutterwaveSyncResult>(response);
    return {
        ...data,
        package_kind: data.package_kind || kind,
        flutterwave_plan_codes: normalizeFlutterwavePlanCodes(data.flutterwave_plan_codes) || undefined,
    };
}

/** @deprecated Prefer actions from redux/adminSubscriptions/actions via useDispatchPromise. */
export { fetchFlutterwaveSyncCatalog, syncFlutterwavePlansAdmin } from '../redux/adminSubscriptions/actions';

export function formatUsdCents(cents: number): string {
    return `$${(Math.round(cents || 0) / 100).toFixed(2)}`;
}

export function countConfiguredPlanCodes(item: FlutterwaveCatalogItem | null | undefined): number {
    if (!item) return 0;
    const map = item.flutterwave_plan_codes;
    if (map && Object.keys(map).length) return Object.keys(map).length;
    return item.flutterwave_plan_code ? 1 : 0;
}
